import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy,
  TrendingUp,
  Target,
  ChevronDown,
  Flame,
  Award,
  Eye
} from 'lucide-react';
import { journeyTracker } from '../lib/journeyTracker';
import type { Achievement } from '../types/learning';

export const ProgressWidget: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Initialize with safe defaults and error handling
  const getInitialStats = () => {
    try {
      return journeyTracker.getStats();
    } catch (error) {
      console.error('Error loading stats:', error);
      return {
        totalFrameworksViewed: 0,
        totalTimeSpent: 0,
        averageSessionTime: 0,
        totalAchievementPoints: 0
      };
    }
  };
  
  const getInitialAchievements = () => {
    try {
      return journeyTracker.getAchievements();
    } catch (error) {
      console.error('Error loading achievements:', error);
      return [];
    }
  };
  
  const getInitialJourney = () => {
    try {
      return journeyTracker.getJourney();
    } catch (error) {
      console.error('Error loading journey:', error);
      return {
        totalAchievementPoints: 0,
        learningStreak: { current: 0 },
        islandProgress: { visitedRegions: new Set() },
        mountainProgress: { reachedElevations: new Set() },
        mofProgress: { exploredLayers: new Set() }
      };
    }
  };
  
  const [stats, setStats] = useState(getInitialStats());
  const [achievements, setAchievements] = useState(getInitialAchievements());
  const [journey, setJourney] = useState(getInitialJourney());

  // Refresh stats periodically
  useEffect(() => {
    const interval = setInterval(() => {
      try {
        setStats(journeyTracker.getStats());
        setAchievements(journeyTracker.getAchievements());
        setJourney(journeyTracker.getJourney());
      } catch (error) {
        console.error('Error refreshing progress widget data:', error);
      }
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Listen for achievement unlocks to update display
  useEffect(() => {
    const handleAchievementUnlock = () => {
      try {
        setAchievements(journeyTracker.getAchievements());
        setJourney(journeyTracker.getJourney());
      } catch (error) {
        console.error('Error handling achievement unlock:', error);
      }
    };

    window.addEventListener('achievement-unlocked', handleAchievementUnlock);
    return () => window.removeEventListener('achievement-unlocked', handleAchievementUnlock);
  }, []);

  const unlockedAchievements = achievements?.filter(a => a.unlockedAt) || [];
  const totalAchievements = achievements?.length || 0;
  const nextAchievement = getNextAchievement();
  const completionPercentage = totalAchievements > 0 
    ? Math.round((unlockedAchievements.length / totalAchievements) * 100) 
    : 0;

  function getNextAchievement(): Achievement | null {
    if (!achievements || achievements.length === 0) return null;
    
    // Find closest achievement to unlocking
    const pending = achievements
      .filter(a => !a.unlockedAt)
      .sort((a, b) => {
        const aProgress = (a.progress / a.maxProgress) * 100;
        const bProgress = (b.progress / b.maxProgress) * 100;
        return bProgress - aProgress;
      });

    return pending[0] || null;
  }

  return (
    <div className="fixed bottom-6 left-6 z-40">
      {/* Collapsed View */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="bg-gradient-to-r from-primary-600 to-sage-600 text-white rounded-full p-4 shadow-2xl hover:shadow-primary-500/50 hover:scale-110 transition-all group relative"
          title="View Your Progress"
        >
          <Trophy className="h-6 w-6 group-hover:rotate-12 transition-transform" />
          
          {/* Points Badge */}
          <div className="absolute -top-2 -right-2 bg-[var(--mustard)] text-white text-xs font-bold rounded-full h-7 w-7 flex items-center justify-center shadow-lg">
            {journey?.totalAchievementPoints || 0}
          </div>

          {/* Streak Badge */}
          {(journey?.learningStreak?.current || 0) > 1 && (
            <div className="absolute -bottom-2 -right-2 bg-coral-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-lg">
              <Flame className="h-3 w-3" />
            </div>
          )}
        </button>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div className="bg-[var(--bg-raised)] shadow-2xl border-2 border-primary-200 w-80 overflow-hidden animate-fadeIn">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-sage-600 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Trophy className="h-6 w-6 text-white" />
                <h3 className="text-white font-bold text-lg">Your Progress</h3>
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 hover:bg-[var(--bg-raised)]/20 rounded transition-colors"
              >
                <ChevronDown className="h-5 w-5 text-white" />
              </button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-[var(--bg-raised)]/20 p-2 text-center">
                <div className="text-2xl font-bold text-white">{journey?.totalAchievementPoints || 0}</div>
                <div className="text-xs text-white/80">Points</div>
              </div>
              <div className="bg-[var(--bg-raised)]/20 p-2 text-center">
                <div className="text-2xl font-bold text-white">{unlockedAchievements.length}</div>
                <div className="text-xs text-white/80">Achievements</div>
              </div>
              <div className="bg-[var(--bg-raised)]/20 p-2 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Flame className="h-4 w-4 text-[var(--mustard)]" />
                  <div className="text-2xl font-bold text-white">{journey?.learningStreak?.current || 0}</div>
                </div>
                <div className="text-xs text-white/80">Day Streak</div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
            {/* Overall Completion */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-charcoal-700 flex items-center gap-2">
                  <Award className="h-4 w-4 text-primary-600" />
                  Overall Progress
                </span>
                <span className="text-sm font-bold text-primary-600">{completionPercentage}%</span>
              </div>
              <div className="w-full bg-[var(--hover-tint)] rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary-500 to-sage-500 transition-all duration-500"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
            </div>

            {/* Next Achievement */}
            {nextAchievement && (
              <div className="bg-gradient-to-r from-amber-50 to-coral-50 p-3 border border-[var(--mustard)]">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-4 w-4 text-[var(--mustard)]" />
                  <span className="text-xs font-semibold text-[var(--mustard)]">Next Achievement</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{nextAchievement.icon}</span>
                  <div className="flex-1">
                    <h4 className="text-sm font-bold text-charcoal-900 mb-1">
                      {nextAchievement.name}
                    </h4>
                    <p className="text-xs text-charcoal-600 mb-2">
                      {nextAchievement.description}
                    </p>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-charcoal-600">Progress</span>
                      <span className="text-xs font-semibold text-charcoal-900">
                        {nextAchievement.progress}/{nextAchievement.maxProgress}
                      </span>
                    </div>
                    <div className="w-full bg-[var(--bg-raised)] rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-500 to-coral-500 transition-all duration-500"
                        style={{ width: `${(nextAchievement.progress / nextAchievement.maxProgress) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Category Progress */}
            <div>
              <h4 className="text-xs font-semibold text-charcoal-700 mb-2 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-sage-600" />
                Exploration Stats
              </h4>
              <div className="space-y-2">
                <ProgressBar
                  label="Island Regions"
                  icon="🏝️"
                  current={journey?.islandProgress?.visitedRegions?.size || 0}
                  max={12}
                  color="from-sage-500 to-primary-500"
                />
                <ProgressBar
                  label="Mountain Elevations"
                  icon="🏔️"
                  current={journey?.mountainProgress?.reachedElevations?.size || 0}
                  max={5}
                  color="from-primary-500 to-sage-500"
                />
                <ProgressBar
                  label="MOF Layers"
                  icon="🏗️"
                  current={journey?.mofProgress?.exploredLayers?.size || 0}
                  max={5}
                  color="from-amber-500 to-coral-500"
                />
                <ProgressBar
                  label="Frameworks Viewed"
                  icon="📊"
                  current={stats?.totalFrameworksViewed || 0}
                  max={20}
                  color="from-coral-500 to-amber-500"
                />
              </div>
            </div>

            {/* Recent Achievements */}
            {unlockedAchievements.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-charcoal-700 mb-2 flex items-center gap-2">
                  <Eye className="h-4 w-4 text-primary-600" />
                  Recent Achievements
                </h4>
                <div className="space-y-2">
                  {unlockedAchievements
                    .sort((a, b) => (b.unlockedAt?.getTime() || 0) - (a.unlockedAt?.getTime() || 0))
                    .slice(0, 3)
                    .map((achievement) => (
                      <div
                        key={achievement.id}
                        className="flex items-center gap-2 p-2 bg-primary-50"
                      >
                        <span className="text-xl">{achievement.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-charcoal-900 truncate">
                            {achievement.name}
                          </p>
                          <p className="text-xs text-charcoal-600">
                            {achievement.tier.charAt(0).toUpperCase() + achievement.tier.slice(1)} • {' '}
                            {achievement.unlockedAt && new Date(achievement.unlockedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 bg-primary-50 border-t-2 border-primary-200">
            <Link
              to="/elevation/progress"
              className="block w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors text-center"
            >
              View Full Dashboard
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Component for Progress Bars
interface ProgressBarProps {
  label: string;
  icon: string;
  current: number;
  max: number;
  color: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ label, icon, current, max, color }) => {
  const percentage = Math.min((current / max) * 100, 100);
  
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-charcoal-600 flex items-center gap-1">
          <span>{icon}</span>
          {label}
        </span>
        <span className="text-xs font-semibold text-charcoal-900">
          {current}/{max}
        </span>
      </div>
      <div className="w-full bg-[var(--hover-tint)] rounded-full h-2 overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${color} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

